import merge from 'lodash.merge';
import { action, computed, makeObservable } from 'mobx';

import { ManyToMany, ManyToOne, OneToMany } from '../../core/engine/decorators';
import { api } from '../../api';
import { catchError } from '../../core/catch-error';
import { getFullName } from '../../lib/utils/get-full-name';
import { getNameInitials } from '../../lib/utils/get-name-initials';
import { Model } from '../../core/engine';
import { Task } from './';

import type { Space, User, Project } from './';
import { toastPool } from '../../features/toasts/models/toast-pool';
import { Toast } from '../../features/toasts/models/toast';

export class Member extends Model {
  displayName: string;
  userId: string;
  role: string;
  email: string;
  insertedAt: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  status?: string;
  membershipRequested: boolean;
  creditsRequested?: boolean;
  invitedAt?: string;

  flags?: {
    'organization_settings.member_management': boolean;
    'organization_settings.payment_management': boolean;
    'organization_settings.storage_request': boolean;
    'organization_settings.company_data': boolean;
    'project.create': boolean;
    'project.share': boolean;
    'project.collaborator_management': boolean;
  };

  preferences: {
    storyBoardPreferences: {
      zoom: number;
    };
    projectsDisplayLayout?: 'list' | 'grid';
    stepsUI: Record<
      string,
      {
        displayLayout?: 'list' | 'grid';
        order?: string;
        showHiddenShots?: boolean;
      }
    >;
  };

  spaceId: string;

  @ManyToOne('members')
  user: User;

  @ManyToOne('members')
  space: Space;

  @OneToMany('assignee')
  tasks: Task[] = [];

  @ManyToMany('collaborators')
  projects: Project[] = [];

  @OneToMany('comments')
  comments: Comment[] = [];

  constructor() {
    super('members');

    this.membershipRequested = false;
    this.preferences = {
      stepsUI: {},
      projectsDisplayLayout: 'grid',
      storyBoardPreferences: {
        zoom: 1,
      },
    };

    makeObservable(this, {
      displayName: true,
      role: true,
      email: true,
      firstName: true,
      lastName: true,
      avatar: true,
      status: true,
      user: true,
      space: true,
      preferences: true,
      requestMembership: action,
      fullName: computed,
      initials: computed,
      toggleShowHiddenShots: action,
    });
  }

  get fullName() {
    return getFullName(this) || this.email;
  }

  get initials() {
    return getNameInitials(this) || this.email[0];
  }

  async resend() {
    await api.put(`/spaces/${this.spaceId}/members/${this._id}/resend`);
    const invitedAtDate = new Date();
    this.invitedAt = invitedAtDate.toISOString();
  }

  override async update(values: Partial<Omit<this, '_id'>>): Promise<void> {
    await api.patch(`/spaces/${this.spaceId}/members/${this._id}`, values);
    merge(this, values);
  }

  toggleShowHiddenShots(stepId: string) {
    const stepPreferences = this.preferences.stepsUI?.[stepId];
    const newPreferences = { ...this.preferences.stepsUI };

    if (!stepPreferences) {
      newPreferences[stepId] = {
        showHiddenShots: false,
      };
    } else {
      newPreferences[stepId].showHiddenShots = stepPreferences.showHiddenShots === false;
    }

    this.update({
      preferences: {
        ...this.preferences,
        stepsUI: newPreferences,
      },
    } as Partial<Omit<this, '_id'>>);
  }

  updateStepLayout(id: string, layout: 'list' | 'grid'): Promise<void> {
    return this.update({
      preferences: {
        ...this.preferences,
        stepsUI: {
          ...this.preferences?.stepsUI,
          [id]: {
            ...this.preferences?.stepsUI?.[id],
            displayLayout: layout,
          },
        },
      },
    } as Partial<Omit<this, '_id'>>);
  }

  override async delete(): Promise<void> {
    const shouldUpdateSeat =
      this.space && this.space.hasSubscription() && !this.space.isSelfServing;

    if (shouldUpdateSeat) {
      this.space.seats!.current--;
    }

    try {
      await super.delete(`/spaces/${this.spaceId}/members/${this._id}`);
      toastPool.insert(new Toast('Access revoked'));
    } catch (e) {
      if (shouldUpdateSeat) {
        this.space.seats!.current++;
      }
    }
  }

  async requestMembership(): Promise<void> {
    try {
      const { data } = await api.get(
        `/spaces/${this.spaceId}/members/${this._id}/request-membership`,
      );
      this.membershipRequested = data.membershipRequested;
    } catch (e) {
      catchError(e);
    }
  }

  async promote(): Promise<void> {
    try {
      const { data } = await api.get(`/spaces/${this.spaceId}/members/${this._id}/promote`);
      this.membershipRequested = data.membershipRequested;
      this.role = data.role;
    } catch (e) {
      catchError(e);
    }
  }

  static getAll(): Member[] {
    return Model.getAll().filter((model) => model.$name === 'members') as Member[];
  }

  static getOne(id: string): Member | undefined {
    return Model.getOne(id) as Member;
  }

  toPOJO(): Record<string, any> {
    return {};
  }

  changeZoomLevel(zoomLevel: number) {
    if (zoomLevel < 1 || zoomLevel > 6) return;

    this.update({
      preferences: {
        ...this.preferences,
        storyBoardPreferences: {
          ...this.preferences.storyBoardPreferences,
          zoom: zoomLevel,
        },
      },
    } as Partial<Omit<this, '_id'>>);
  }

  changeStepOrder(stepId: string, sortBy: string) {
    const stepSettings = this.preferences.stepsUI?.[stepId];

    if (!stepSettings) {
      this.preferences.stepsUI[stepId] = {};
    }

    return this.update({
      preferences: {
        ...this.preferences,
        stepsUI: {
          ...this.preferences.stepsUI,
          [stepId]: {
            ...this.preferences.stepsUI[stepId],
            order: sortBy,
          },
        },
      },
    } as Partial<Omit<this, '_id'>>);
  }

  async requestCredits() {
    try {
      await api.get(`/spaces/${this.spaceId}/members/${this._id}/request-credits`);
      this.creditsRequested = true;
    } catch (e) {
      catchError(e);
    }
  }
}
